<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import {
    Alert,
    Button,
    ButtonDropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav
  } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { BulkActionModal } from '@client/components/bulk-action-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';
  import { RouteSelectionControls } from '@client/components/route-selection-controls';

  import { Tab } from '@client/pages/bizcards/components';

  import { BizCardsService } from '@client/services/bizcards';
  import { BizCardsRoutingService } from '@client/services/bizcards-routing';
  import { GroupsService } from '@client/services/groups';

  import { createSelection, selectedItems } from '@client/utils/selection';

  import { deepCopyArray } from '@common/utils/arrays';
  import { generatePastelColors } from '@common/utils/colors';

  import { DiagnosticsModal, EditModal } from './modals';
  import { Route } from './route';
  import { routeRunners } from './shared';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();

  const queryParams = {};

  let routes = [];
  let priority = [];
  let reordering = false;
  let dropIndex = null;
  let startIndex = null;
  let isSaving = false;

  let groups = [];
  let senderGroups = [];
  let groupOptions = [];
  let businessCardOptions = [];

  let startPosition = null;
  let targetPosition = null;

  let initialized = false;

  let showDiagnosticModal = false;
  let selectedRoute = null;

  // Factory, not a constant: each reset gets a fresh object to bind to.
  const emptyStatusFilter = () => ({
    enabled: { label: 'label.ENABLED', value: 'enabled', enabled: false },
    disabled: { label: 'label.DISABLED', value: 'disabled', enabled: false }
  });

  let statusFilter = emptyStatusFilter();

  const getRouteKey = ({ _id }) => _id;
  const selection = createSelection(getRouteKey);

  const BULK_NOTIFY = {
    enable: 'label.BULK_ACTION.NOTIFY_ENABLE_ROUTES',
    disable: 'label.BULK_ACTION.NOTIFY_DISABLE_ROUTES',
    delete: 'label.BULK_ACTION.NOTIFY_DELETE_ROUTES'
  };

  let selecting = false;
  let showBulkAction = false;
  let bulkAction = '';
  let bulkItems = [];

  const queryFn = async () => {
    const response = await BizCardsRoutingService.getAll();
    const colors = generatePastelColors(response.length);

    if (!groups.length) {
      groups = await GroupsService.getAll();
    }

    routes = response.map((signature, index) => {
      const sendergroups = groups.filter(
        ({ groupname }) => signature.sendergroups.filter((group) => groupname === group.groupname).length
      );

      return {
        ...signature,
        priority: index + 1,
        color: colors[index],
        selectedGroups: sendergroups.map(({ groupname }) => ({ label: groupname, value: groupname })),
        sendergroups
      };
    });

    groupOptions = groups.map(({ groupname }) => ({ label: groupname, value: groupname }));

    priority = deepCopyArray(routes);

    initialized = true;

    return routes;
  };

  const query = createQuery({ queryKey: ['routes', queryParams], queryFn });

  const updatePriorityPositions = (list, start, target) => {
    if (start < target) {
      list.splice(target + 1, 0, list[start]);
      list.splice(start, 1);
    } else {
      if (target === -1) {
        list.splice(start - 1, 1);
      } else {
        list.splice(target, 0, list[start]);
        list.splice(start + 1, 1);
      }
    }

    list.forEach((item, index) => {
      item.priority = index + 1;
    });

    return list;
  };

  const updatePriority = (which) => {
    priority = deepCopyArray(which === 'routes' ? routes : priority);

    if (which !== 'routes') {
      routes = deepCopyArray(priority);
    }
  };

  // Filter and reorder cannot both be live: drag and manual move index into the
  // full priority list, so entering reorder drops the filter.
  const onStartReorder = () => {
    statusFilter = emptyStatusFilter();
    reordering = true;
  };

  const onCancelReorder = () => {
    reordering = false;
    updatePriority('routes');
  };

  const onSaveReorder = async () => {
    isSaving = true;
    const ids = priority.map(({ _id }) => _id);
    const response = await BizCardsRoutingService.saveRouteOrder(ids);

    if (response.status >= 400) {
      addNotification({
        text: $format('label.FAILURE.UPDATE_ROUTING_PRIORITY'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    } else {
      updatePriority('priority');

      addNotification({
        text: $format('label.SUCCESS.UPDATE_ROUTING_PRIORITY'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
    }

    isSaving = reordering = false;
  };

  const updatePositions = () => {
    priority = updatePriorityPositions(priority, Number(startPosition) - 1, Number(targetPosition) - 1);
    startPosition = targetPosition = null;
  };

  const onAddRoute = () => {
    selectedRoute = {
      blockname: '',
      priority: 0,
      editing: true,
      status: false,
      sendergroups: [],
      selectedGroups: []
    };
  };

  const onDelete = async () => {
    priority.forEach((item, index) => {
      item.priority = index + 1;
    });

    const ids = priority.map(({ _id }) => _id);
    const response = await BizCardsRoutingService.saveRouteOrder(ids);

    if (response.status >= 400) {
      addNotification({
        text: $format('label.FAILURE.DELETE_ROUTING_PRIORITY'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }

    queryFn();
  };

  // Enable and Disable act only on the routes that need changing, so a mixed
  // selection can offer both without re-saving routes that are already correct.
  const onBulkAction = (action) => {
    bulkItems =
      action === 'delete'
        ? selectedRoutes
        : selectedRoutes.filter(({ status }) => (action === 'enable' ? !status : status));
    bulkAction = action;
    showBulkAction = true;
  };

  const onBulkActionFinish = async (failed) => {
    const succeeded = bulkItems.length - failed.length;

    if (succeeded) {
      addNotification({
        text: $format(BULK_NOTIFY[bulkAction], { values: { count: succeeded } }),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
    }

    // Leave only the failures selected so they can be retried directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    const remaining = await queryFn();

    // The server does not renumber on delete, so re-save the order once for the
    // whole batch rather than once per route.
    if (bulkAction === 'delete' && succeeded) {
      await BizCardsRoutingService.saveRouteOrder(remaining.map(getRouteKey));
    }
  };

  onMount(async () => {
    const bizCards = await BizCardsService.getAll();
    const cards = Array.isArray(bizCards) ? bizCards : [];
    businessCardOptions = cards.map(({ rulename }) => rulename).sort((a, b) => a.localeCompare(b));
  });

  $: senderGroups = groups;
  $: routes = $query?.data || [];
  $: hasRouting = initialized && routes.length > 0;
  $: selectedStatus = Object.values(statusFilter)
    .map(({ enabled, value }) => enabled && value)
    .filter(Boolean);
  $: matchesStatus = ({ status }) => !selectedStatus.length || selectedStatus.includes(status ? 'enabled' : 'disabled');
  $: visibleRoutes = routes.filter(matchesStatus);
  $: selectedRoutes = selectedItems(routes, $selection, getRouteKey);
  // Keeps the count honest when a refetch or filter change drops selected routes.
  $: selecting && selection.prune(visibleRoutes);
  $: bulkConfig =
    {
      enable: {
        title: $format('label.BULK_ACTION.CONFIRM_ENABLE_ROUTES', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_ENABLE_ROUTES'),
        confirmLabel: $format('label.ENABLE'),
        confirmColor: 'primary'
      },
      disable: {
        title: $format('label.BULK_ACTION.CONFIRM_DISABLE_ROUTES', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_DISABLE_ROUTES'),
        confirmLabel: $format('label.DISABLE'),
        confirmColor: 'primary'
      },
      delete: {
        title: $format('label.BULK_ACTION.CONFIRM_DELETE_ROUTES', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_DELETE_ROUTES'),
        confirmLabel: $format('label.DELETE'),
        confirmColor: 'danger',
        progressKey: 'label.BULK_ACTION.PROGRESS_DELETE_ROUTES'
      }
    }[bulkAction] || {};
  $: disableManualMove =
    !startPosition ||
    !targetPosition ||
    Number(startPosition) === Number(targetPosition) ||
    Number(targetPosition) > priority.length ||
    Number(startPosition) < 1 ||
    Number(targetPosition) < 1;
</script>

<div id="routes" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <PageHeader
    title={$format('label.ROUTING')}
    paths={[
      [$format('label.BUSINESS_CARDS'), '/bizcards/studio'],
      [$format('label.ASSIGNMENT'), '/bizcards/assignment'],
      [$format('label.ROUTING')]
    ]}
  >
    <div class="d-flex justify-content-end align-items-center gap-2">
      {#if initialized && !reordering}
        <ButtonDropdown class="routing-actions hide--mobile">
          <DropdownToggle color="secondary" caret>{$format('label.RESOURCES')}</DropdownToggle>
          <DropdownMenu>
            <DropdownItem on:click={() => (showDiagnosticModal = true)}>
              {$format('label.ROUTING_BCARD_DIAGNOSTICS')}
            </DropdownItem>
          </DropdownMenu>
        </ButtonDropdown>

        <Button color="primary" on:click={onAddRoute}>{$format('label.ADD_ROUTE')}</Button>
      {/if}
    </div>
  </PageHeader>
  <div class="page-content">
    <Nav pills>
      <Tab on:click={() => navigate('/bizcards/assignment')}>{$format('label.ASSIGNMENT')}</Tab>
      <Tab active on:click={() => navigate('/bizcards/assignment/routing')}>{$format('label.ROUTING')}</Tab>
      <Tab on:click={() => navigate('/bizcards/assignment/history')}>{$format('label.HISTORY')}</Tab>
    </Nav>
    <div class="routes-layout">
      {#if hasRouting}
        <Alert color="light" class="routes-info">
          <Icon name="info" />
          <p>{$format('label.ROUTES_BCARDS_INFO')}</p>
        </Alert>

        <div class="routes-controls sticky-header">
          <div class="filters">
            {#if !reordering}
              <MultiSelectFilter
                bind:filter={statusFilter}
                param="status"
                label={$format('label.STATUS')}
                tooltipLabel={$format('label.SELECTED_STATUS')}
              />
            {/if}
          </div>
          <div class="actions">
            {#if !reordering}
              <RouteSelectionControls
                bind:selecting
                {selection}
                rows={visibleRoutes}
                selected={selectedRoutes}
                id="bizcard-routes"
                onAction={onBulkAction}
                onSelectAll={() => selection.selectAll(visibleRoutes)}
              />
            {/if}

            {#if !reordering && !selecting}
              <Button color="secondary" on:click={onStartReorder}>
                {@html $format('label.CHANGE_PRIORITY')}
              </Button>
            {:else if reordering}
              <Button color="secondary" disabled={isSaving} on:click={onCancelReorder}>
                {$format('label.CANCEL')}
              </Button>
              <Button color="primary" disabled={isSaving} on:click={onSaveReorder}>
                {$format('label.SAVE')}
              </Button>
            {/if}
          </div>
        </div>

        <div class="priority">
          <div class="priority-list">
            {#if reordering}
              <div class="manual-move">
                <span>{$format('label.MOVE')}</span>
                <input type="number" bind:value={startPosition} min="1" max={priority.length} />
                <span>{$format('label.MOVE_TO')}</span>
                <span class="hide--mobile">{$format('label.POSITION').toLowerCase()}</span>
                <input type="number" bind:value={targetPosition} min="1" max={priority.length} />
                <Button size="sm" color="secondary" on:click={updatePositions} disabled={disableManualMove}>
                  {$format('label.UPDATE')}
                </Button>
              </div>
            {/if}
            <!-- Gated in place so routeIndex stays a true index into priority. -->
            {#each priority as route, index}
              {#if matchesStatus(route)}
                <Route
                  bind:reordering
                  bind:priority
                  bind:dropIndex
                  bind:startIndex
                  bind:senderGroups
                  bind:selectedRoute
                  routeIndex={index}
                  onRefresh={queryFn}
                  {onDelete}
                  {route}
                  {updatePriorityPositions}
                  {businessCardOptions}
                  {selecting}
                  selected={$selection.has(route._id)}
                  onSelect={selection.toggle}
                />
              {/if}
            {/each}
          </div>
          {#if !visibleRoutes.length}
            <NoMatches message={$format('label.MESSAGE.NO_MATCHES_ROUTES')} />
          {/if}
        </div>
      {:else if initialized}
        <EmptyState
          icon="git-pull-request"
          heading={$format('label.EMPTY_STATE.BUSINESS_CARD_ROUTING_HEADING')}
          description={$format('label.EMPTY_STATE.BUSINESS_CARD_ROUTING_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.BUSINESS_CARD_ROUTING_ACTION')}
          on:click={onAddRoute}
        />
      {/if}
    </div>
  </div>
</div>

{#if showBulkAction}
  <BulkActionModal
    bind:show={showBulkAction}
    items={bulkItems}
    title={bulkConfig.title}
    warning={bulkConfig.warning}
    confirmLabel={bulkConfig.confirmLabel}
    confirmColor={bulkConfig.confirmColor}
    progressKey={bulkConfig.progressKey || 'label.BULK_ACTION.PROGRESS'}
    leaveMessage={$format('label.BULK_ACTION.CONFIRM_LEAVE_ROUTES')}
    getName={({ blockname }) => blockname}
    onRun={(route) => routeRunners[bulkAction](route, senderGroups)}
    onFinish={onBulkActionFinish}
  />
{/if}

<DiagnosticsModal bind:show={showDiagnosticModal} />

{#if selectedRoute}
  <EditModal
    bind:priority
    bind:senderGroups
    show={selectedRoute}
    bind:route={selectedRoute}
    onAdd={onSaveReorder}
    {updatePriorityPositions}
    {groupOptions}
    {businessCardOptions}
  />
{/if}

<style lang="scss" src="./routing.scss"></style>
